import { exportImage } from './export-image-core';

export interface ImagePositionOptions {
  positionX: number;
  positionY: number;
  width: number;
  height: number;
}

/**
 * Exports a cropped portion of an element based on position and dimensions
 */
export async function exportImagePosition({
  element,
  imageOptions,
  pixelRatio = 4,
}: {
  element: HTMLElement;
  imageOptions: ImagePositionOptions;
  pixelRatio: number;
}): Promise<string> {
  const dataUrl = await exportImage({ element, pixelRatio });

  const { positionX, positionY, width, height } = imageOptions;

  // Create an image element to load the full exported image
  const img = new Image();
  img.src = dataUrl;

  // Wait for image to load with timeout
  await new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('Image load timeout'));
    }, 30000); // 30 second timeout

    img.onload = () => {
      clearTimeout(timeout);
      resolve();
    };
    img.onerror = (error) => {
      clearTimeout(timeout);
      reject(new Error('Failed to load image for export'));
    };
  });

  // Create canvas to crop the image
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { alpha: false });

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Enable high-quality image smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Calculate dimensions with pixel ratio
  const scaledX = positionX * pixelRatio;
  const scaledY = positionY * pixelRatio;
  const scaledWidth = width * pixelRatio;
  const scaledHeight = height * pixelRatio;

  // Set canvas size to the cropped dimensions
  canvas.width = scaledWidth;
  canvas.height = scaledHeight;

  // Fill with white background for better quality
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, scaledWidth, scaledHeight);

  // Draw the cropped portion of the image
  ctx.drawImage(
    img,
    scaledX,
    scaledY,
    scaledWidth,
    scaledHeight,
    0,
    0,
    scaledWidth,
    scaledHeight,
  );

  // Convert canvas to dataUrl with maximum quality
  return canvas.toDataURL('image/png', 1);
}
